import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface RepeatEditConfirmModalProps {
  open: boolean;
  /** このバッジだけ変更し、以降の伝播から外す。 */
  onIndividual: () => void;
  /** 通常のグループ編集として、繰り返す全出現へ適用する。 */
  onAll: () => void;
  /** 背景クリック / Escape での中断 (任意)。 */
  onClose?: () => void;
}

/**
 * issue #166: 繰り返しフレーズの伝播で自動変更されたバッジをユーザが編集しようとしたときに、
 * 「個別に編集」か「全体に適用」かを確認する。 DiscardConfirmModal と同じ portal / dialog 構造・
 * Tailwind semantic クラスを踏襲し、 譜面エディタ内のモーダル体験を統一する。
 */
const RepeatEditConfirmModal: React.FC<RepeatEditConfirmModalProps> = ({
  open,
  onIndividual,
  onAll,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="repeat-edit-confirm-title"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadein"
      onClick={() => onClose?.()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-md animate-popin"
      >
        <h2 id="repeat-edit-confirm-title" className="text-lg font-bold text-gray-800 mb-2">
          繰り返しの編集
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          別の出現箇所の同じフレーズからの変更が適用されています。個別に編集しますか？
        </p>
        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onIndividual}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            個別に編集
          </button>
          <button
            type="button"
            onClick={onAll}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-colors"
          >
            全体に適用
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RepeatEditConfirmModal;
